// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { Message } from "@/features/assistant/types";
import { getAssistantExpanded, setAssistantExpanded } from "@/lib/preferences";

const THREAD_KEY = "backoffice.assistant.thread.v1";

interface StoredThread {
  /** Who the thread belongs to. A session that ends without an unmount (401 redirect,
   * expired token on reload) leaves the thread behind; the owner check keeps it from the
   * next person who signs into the same tab. */
  owner: string;
  messages: Message[];
}

const isString = (v: unknown): v is string => typeof v === "string";
const isArray = Array.isArray;

/** Enough of a shape check that rendering can't throw on a stale or tampered entry. */
function isMessage(value: unknown): value is Message {
  if (!value || typeof value !== "object") return false;
  const m = value as Record<string, unknown>;
  if (!isString(m.id)) return false;
  if (m.role === "user") return isString(m.text);
  if (m.role === "divider") return isString(m.label);
  if (m.role !== "assistant") return false;
  return (
    isString(m.question) &&
    isString(m.status) &&
    isString(m.text) &&
    isArray(m.steps) &&
    isArray(m.tables) &&
    isArray(m.links) &&
    isArray(m.notes)
  );
}

/** One conversation per browser session. An answer cut off by a reload comes back stopped. */
export function loadThread(owner: string | null): Message[] {
  if (!owner) return [];
  try {
    const raw = sessionStorage.getItem(THREAD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<StoredThread> | null;
    if (!parsed || parsed.owner !== owner || !Array.isArray(parsed.messages)) return [];
    if (!parsed.messages.every(isMessage)) return [];
    return parsed.messages.map((m) =>
      m.role === "assistant" && (m.status === "working" || m.status === "streaming") ? { ...m, status: "stopped" } : m,
    );
  } catch {
    return [];
  }
}

export function saveThread(messages: Message[], owner: string | null): void {
  if (!owner) return;
  try {
    const stored: StoredThread = { owner, messages };
    sessionStorage.setItem(THREAD_KEY, JSON.stringify(stored));
  } catch {
    // Storage full or blocked: the thread still lives in memory for this page load.
  }
}

/** Signing out ends the conversation: the next person in this tab must not inherit it. */
export function clearThread(): void {
  try {
    sessionStorage.removeItem(THREAD_KEY);
  } catch {
    // Blocked storage held nothing to leak.
  }
}

export function loadExpanded(): boolean {
  return getAssistantExpanded();
}

export function saveExpanded(value: boolean): void {
  setAssistantExpanded(value);
}
